import { cn } from "@-/com.npmjs.cn";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useConvertedCharacter } from "@-/dev.improve.haniverse.lib.hanzi-script";
import type { DrillPageHeaderTextsVariant } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.header-texts";
import type { DrillPagePronunciationHintGate } from "#use-drill-page-pronunciation-hint-gate";

/**
 * The central prompt on a pronunciation review, always visible when it exists: it only says
 * WHICH reading is asked for, never the reading itself. A gloss is bold; any other hint is regular
 * weight. A reading confined to a few words (会 kuài) adds "only in 会计" underneath. Only
 * `useDrillPagePronunciationHintGate` decides whether it shows.
 */
export function DrillPagePronunciationHint({
  gate,
  variant,
}: {
  gate: DrillPagePronunciationHintGate;
  variant: DrillPageHeaderTextsVariant;
}) {
  const onlyInWords = gate.hint.onlyInWords ?? [];
  // Usage words are authored in simplified (or already traditional on a traditional-only card).
  const onlyInText = useConvertedCharacter(onlyInWords.join(", "), "simplified");

  // Explicit `text-foreground`: uniwind drops the cva base color once a size class is present.
  return (
    <>
      <Text
        adjustsFontSizeToFit
        className={cn(
          "text-center text-foreground",
          variant === "landscape-compact" ? "text-3xl" : "text-4xl",
          gate.hint.kind === "gloss" ? "font-bold" : "font-normal",
        )}
        numberOfLines={3}
        testID="pronunciation-hint"
      >
        {gate.hint.text}
      </Text>
      {onlyInWords.length > 0 ? (
        <Text
          className={cn(
            "pt-1 text-center text-muted-foreground",
            variant === "landscape-compact" ? "text-lg" : "text-xl",
          )}
          numberOfLines={1}
          testID="pronunciation-hint-only-in"
        >
          only in {onlyInText}
        </Text>
      ) : null}
    </>
  );
}
